import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, Plus, Pencil, Trash2, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const empty = { code: "", title: "", division: "" };

export default function CsiCodes() {
  const [csi, setCsi] = useState([]);
  const [subs, setSubs] = useState([]);
  const [dialog, setDialog] = useState(null); // null | {mode:'add'|'edit', ...form}
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = () => Promise.all([api.get("/csi-codes"), api.get("/subcontractors")])
    .then(([c, s]) => { setCsi(c.data); setSubs(s.data); });
  useEffect(() => { load(); }, []);

  const divisions = useMemo(() => [...new Set(csi.map((c) => c.division || "Custom / Ungrouped"))], [csi]);
  const subsByCode = useMemo(() => {
    const m = {};
    subs.forEach((s) => (s.csi_codes || []).forEach((code) => { (m[code] = m[code] || []).push(s); }));
    return m;
  }, [subs]);

  const openAdd = () => setDialog({ mode: "add", ...empty });
  const openEdit = (c) => setDialog({ mode: "edit", id: c.id, code: c.code, title: c.title, division: c.division || "" });

  const save = async () => {
    if (!dialog.code.trim() || !dialog.title.trim()) { toast.error("Code and name are required"); return; }
    setSaving(true);
    try {
      const payload = { code: dialog.code.trim(), title: dialog.title.trim(), division: dialog.division.trim() };
      if (dialog.mode === "add") {
        await api.post("/csi-codes", payload);
        toast.success(`Added ${payload.code}`);
      } else {
        const { data } = await api.put(`/csi-codes/${dialog.id}`, payload);
        toast.success(data.migrated_subs ? `Updated · migrated ${data.migrated_subs} subcontractor tag(s)` : "Code updated");
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove code ${c.code} (${c.title})?`)) return;
    try {
      await api.delete(`/csi-codes/${c.id}`);
      toast.success(`Removed ${c.code}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const reset = async () => {
    if (!window.confirm("Re-add the standard MasterFormat divisions? Your custom codes are kept.")) return;
    try {
      const { data } = await api.post("/csi-codes/reset", {});
      toast.success(data.added ? `Re-added ${data.added} default code(s)` : "All default codes already present");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader title="CSI Code Catalog" description="Your workspace trade codes — add, rename, or remove to match your standards" />
      <div className="p-8 space-y-6">
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={openAdd} data-testid="add-csi-btn"><Plus className="h-4 w-4 mr-1" />Add code</Button>
            <Button variant="outline" onClick={reset} data-testid="reset-csi-btn"><RotateCcw className="h-4 w-4 mr-1" />Reset to defaults</Button>
          </div>
        )}

        {divisions.map((div) => (
          <div key={div}>
            <h2 className="font-display font-bold text-sm uppercase tracking-widest text-muted-foreground mb-2">{div}</h2>
            <Card className="border border-border overflow-hidden">
              <Accordion type="multiple">
                {csi.filter((c) => (c.division || "Custom / Ungrouped") === div).map((c) => {
                  const list = subsByCode[c.code] || [];
                  return (
                    <AccordionItem key={c.id || c.code} value={c.id || c.code} className="border-b border-border last:border-0">
                      <div className="flex items-center">
                        <AccordionTrigger className="flex-1 px-4 hover:bg-accent/50" data-testid={`csi-${c.code.replace(/ /g, '')}`}>
                          <div className="flex items-center gap-3 text-left">
                            <span className="font-mono text-xs bg-secondary px-2 py-1 rounded">{c.code}</span>
                            <span className="font-medium">{c.title}</span>
                            <Badge variant={list.length ? "default" : "secondary"}>{list.length} subs</Badge>
                          </div>
                        </AccordionTrigger>
                        {isAdmin && (
                          <div className="flex items-center gap-1 pr-3">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-csi-${c.code.replace(/ /g, '')}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(c)} data-testid={`delete-csi-${c.code.replace(/ /g, '')}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        )}
                      </div>
                      <AccordionContent className="px-4 pb-4">
                        {list.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No subcontractors tagged with this code yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {list.map((s) => (
                              <div key={s.id} className="flex items-center justify-between text-sm border-b border-border/60 pb-2">
                                <div><span className="font-medium">{s.name}</span> <span className="text-muted-foreground">· {s.company_name} · {s.email}</span></div>
                              </div>
                            ))}
                            <Button size="sm" variant="outline" className="mt-2" onClick={() => navigate("/send-itb", { state: { csi_codes: [c.code] } })} data-testid={`send-itb-${c.code.replace(/ /g, '')}`}>
                              <Send className="h-3.5 w-3.5 mr-1" />Send ITB for this code
                            </Button>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </Card>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="csi-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "add" ? "Add CSI code" : "Edit CSI code"}</DialogTitle>
            <DialogDescription>{dialog?.mode === "edit" ? "Renaming the code auto-updates every subcontractor tagged with it." : "Add a custom trade code for this workspace."}</DialogDescription>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div><Label className="text-xs uppercase tracking-widest">Code</Label><Input value={dialog.code} onChange={(e) => setDialog({ ...dialog, code: e.target.value })} className="mt-1.5 font-mono" placeholder="26 00 00 or E-100" data-testid="csi-code-input" /></div>
              <div><Label className="text-xs uppercase tracking-widest">Name</Label><Input value={dialog.title} onChange={(e) => setDialog({ ...dialog, title: e.target.value })} className="mt-1.5" placeholder="Electrical" data-testid="csi-title-input" /></div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Division / group (optional)</Label>
                <Input value={dialog.division} onChange={(e) => setDialog({ ...dialog, division: e.target.value })} className="mt-1.5" placeholder="26 - Electrical" list="csi-divisions" data-testid="csi-division-input" />
                <datalist id="csi-divisions">{divisions.map((d) => <option key={d} value={d} />)}</datalist>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="csi-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="csi-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
